// crewbit-admin/src/components/money/MoneyHero.tsx
//
// Arc C3 P3 — admin Money-home headline (clears §Money.OverviewMoneyHero).
// Mirrors crewbit-mobile/components/money/HeroAmount.tsx: `kind` selects the
// source field + subtitle shape. Reads the SAME summary.data the 4-card strip
// reads, so the headline can never diverge from the strip's numbers.
//
//   revenue_month     → money_in + PoP vs prior_money_in + period label
//   outstanding       → outstanding.total + count/oldest subtitle (no PoP)
//   available_balance → cents/100 + "Connect Stripe" disabled subtitle when null
//                       (Stripe cache is post-launch; v1 always renders disabled)
//   net_30d           → net + PoP vs prior_net + period label
//
// Adds the admin-only headline selector (a small "change headline" control that
// writes moneyHeroDefault) — mobile sets this in Settings; admin sets it inline.

import { useState } from 'react';
import { ArrowDownRight, ArrowUpRight, ChevronDown } from 'lucide-react';
import type { FinancialSummary } from '../../hooks/useFinancialSummary';
import type { MoneyHeroDefault } from '../../hooks/useUserPreferences';
import { DropdownMenu } from '../common/DropdownMenu';
import { formatMoney } from '../../lib/money';
import { admin, palette, radii, spacing, transition, typography } from '../../styles/tokens';

interface MoneyHeroProps {
  kind: MoneyHeroDefault;
  data: FinancialSummary;
  onKindChange: (kind: MoneyHeroDefault) => void;
}

interface PoPDelta {
  pct: number;
  direction: 'up' | 'down';
}

function computeDelta(current: number, prior: number | null): PoPDelta | null {
  if (prior === null) return null;
  if (prior === 0) return null;
  const pct = ((current - prior) / Math.abs(prior)) * 100;
  if (!Number.isFinite(pct)) return null;
  if (Math.abs(pct) < 0.5) return null;
  return { pct, direction: pct >= 0 ? 'up' : 'down' };
}

const HERO_OPTIONS: { key: MoneyHeroDefault; label: string }[] = [
  { key: 'revenue_month', label: 'Money in' },
  { key: 'net_30d', label: 'Net' },
  { key: 'outstanding', label: 'Outstanding' },
  { key: 'available_balance', label: 'Available' },
];

interface HeroView {
  label: string;
  amount: string;
  delta: PoPDelta | null;
  countSubtitle: string | null;
  disabled: boolean;
}

function buildView(kind: MoneyHeroDefault, data: FinancialSummary): HeroView {
  const periodLabel = data.period.label;
  switch (kind) {
    case 'revenue_month':
      return {
        label: `Money in · ${periodLabel}`,
        amount: formatMoney(data.money_in),
        delta: computeDelta(data.money_in, data.prior_money_in),
        countSubtitle: null,
        disabled: false,
      };
    case 'outstanding': {
      const o = data.outstanding;
      const sub =
        o.invoice_count > 0
          ? `${o.invoice_count} invoice${o.invoice_count === 1 ? '' : 's'} · oldest ${o.oldest_days}d`
          : null;
      return { label: 'Outstanding', amount: formatMoney(o.total), delta: null, countSubtitle: sub, disabled: false };
    }
    case 'available_balance': {
      const cents = data.available_balance_cents;
      const isNull = cents === null;
      return {
        label: 'Available',
        amount: formatMoney(isNull ? 0 : cents / 100),
        delta: null,
        countSubtitle: isNull ? 'Connect Stripe' : null,
        disabled: isNull,
      };
    }
    case 'net_30d':
      return {
        label: `Net · ${periodLabel}`,
        amount: formatMoney(data.net),
        delta: computeDelta(data.net, data.prior_net),
        countSubtitle: null,
        disabled: false,
      };
  }
}

export function MoneyHero({ kind, data, onKindChange }: MoneyHeroProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const view = buildView(kind, data);
  const currentLabel = HERO_OPTIONS.find((o) => o.key === kind)?.label ?? 'Money in';

  return (
    <div style={{ marginBottom: spacing.lg }}>
      {/* Label row + headline selector */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: 4 }}>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="Change headline number"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 6px',
            marginLeft: -6,
            border: 'none',
            borderRadius: radii.sm,
            background: menuOpen ? palette.g100 : 'transparent',
            cursor: 'pointer',
            fontFamily: 'inherit',
            color: admin.caption,
            transition: `background ${transition.fast}`,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = palette.g100)}
          onMouseLeave={(e) => (e.currentTarget.style.background = menuOpen ? palette.g100 : 'transparent')}
        >
          <span style={{ fontSize: typography.size.caption, fontWeight: typography.weight.medium }}>{view.label}</span>
          <ChevronDown size={13} strokeWidth={2} />
        </button>
        <DropdownMenu
          isOpen={menuOpen}
          onClose={() => setMenuOpen(false)}
          width={200}
          sections={[
            {
              items: HERO_OPTIONS.map((opt) => ({
                label: opt.label,
                subtitle: opt.key === kind ? 'Current headline' : undefined,
                onClick: () => onKindChange(opt.key),
              })),
            },
          ]}
        />
      </div>

      {/* Headline number */}
      <div
        aria-label={`${currentLabel} ${view.amount}`}
        style={{
          fontSize: typography.size.hero,
          fontWeight: typography.weight.bold,
          lineHeight: typography.lineHeight.tight,
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
          color: view.disabled ? palette.g400 : admin.header,
        }}
      >
        {view.amount}
      </div>

      {/* Delta (PoP) or count subtitle */}
      {view.delta !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 4 }}>
          {view.delta.direction === 'up' ? (
            <ArrowUpRight size={14} color={palette.green} strokeWidth={2} />
          ) : (
            <ArrowDownRight size={14} color={palette.red} strokeWidth={2} />
          )}
          <span
            style={{
              fontSize: typography.size.caption,
              fontVariantNumeric: 'tabular-nums',
              color: view.delta.direction === 'up' ? palette.green : palette.red,
            }}
          >
            {view.delta.direction === 'up' ? '+' : ''}
            {view.delta.pct.toFixed(1)}% vs prior
          </span>
        </div>
      )}
      {view.countSubtitle !== null && (
        <div style={{ fontSize: typography.size.caption, color: admin.caption, marginTop: 4 }}>{view.countSubtitle}</div>
      )}
    </div>
  );
}
